/* eslint-disable @typescript-eslint/no-empty-object-type */
import { handleDomBubbleEvent } from '@/utils/dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { cn } from '@/utils/cn';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import {
  InviteAsAdminTicketingInvitationValidationSchema,
  TInviteAsAdminTicketingInvitationValidation,
  useInviteAsAdminTicketingUserInvitationMutation,
} from '../../api/invite-as-admin-ticketing-user.api';
import { GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY } from '../../api/get-list-invitation-ticketing-user.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { Input } from '@/components/_ui/input';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

const TOAST_MESSAGES_INVITE_AS_ADMIN_TICKETING = {
  INVITE_AS_ADMIN_TICKETING_ERROR: 'Error invite as admin ticketing, please try again',
  INVITE_AS_ADMIN_TICKETING_SUCCESS: 'User ticketing invited successfully!',
};

export type TInviteAsAdminTicketingUserModalType = 'InviteAsAdminTicketingUserModal';
export interface IInviteAsAdminTicketingUserModalProps {}
const InviteAsAdminTicketingUserModal = memo((_props: IInviteAsAdminTicketingUserModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();

  // -- Form --
  const form = useForm<TInviteAsAdminTicketingInvitationValidation>({
    resolver: zodResolver(InviteAsAdminTicketingInvitationValidationSchema),
    mode: 'onChange',
  });

  // -- Mutation --
  const mutationInviteAsAdminTicketingUserInvitation = useInviteAsAdminTicketingUserInvitationMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || TOAST_MESSAGES_INVITE_AS_ADMIN_TICKETING.INVITE_AS_ADMIN_TICKETING_ERROR;
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY] });
        form.reset();
        modalActions.closeModal('InviteAsAdminTicketingUserModal');
        showToast({
          message: TOAST_MESSAGES_INVITE_AS_ADMIN_TICKETING.INVITE_AS_ADMIN_TICKETING_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenInviteAsAdminTicketingUserModal = useMemo(() => {
    return modalActions.checkIsOpenModal('InviteAsAdminTicketingUserModal');
  }, [modalActions]);

  const isLoadingForm = useMemo(() => {
    return mutationInviteAsAdminTicketingUserInvitation.isPending || form.formState.isSubmitting;
  }, [mutationInviteAsAdminTicketingUserInvitation.isPending, form.formState.isSubmitting]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('InviteAsAdminTicketingUserModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  const handleSubmitForm = useCallback(
    (data: TInviteAsAdminTicketingInvitationValidation) => {
      mutationInviteAsAdminTicketingUserInvitation.mutate(data);
    },
    [mutationInviteAsAdminTicketingUserInvitation]
  );

  const isDisabledForm = useMemo(() => {
    return form.formState.isSubmitting || mutationInviteAsAdminTicketingUserInvitation.isPending;
  }, [form.formState.isSubmitting, mutationInviteAsAdminTicketingUserInvitation.isPending]);

  return (
    <Modal
      isOpen={isOpenInviteAsAdminTicketingUserModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[476px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Invite People</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          id="invite-ticketing-user-invitation-form"
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
          className="flex flex-col gap-[16px]"
        >
          <label htmlFor="" className="text-[12px] block font-[400] text-[#9D9D9C]">
            Invite people to join
          </label>
          <div className="flex flex-col gap-[8px]">
            <div>
              <label htmlFor="fullname" className="text-[12px] font-[400] text-[#9D9D9C]">
                Full Name
              </label>
              <Input
                id="fullname"
                placeholder="Enter Full Name"
                {...form.register('fullname')}
                className={cn(form.formState.errors.fullname && 'border-red-500')}
              />
              {form.formState.errors.fullname && (
                <p className="text-red-500 text-[12px] mt-1">{form.formState.errors.fullname.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="text-[12px] font-[400] text-[#9D9D9C]">
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Enter Email"
                {...form.register('email')}
                className={cn(form.formState.errors.email && 'border-red-500')}
              />
              {form.formState.errors.email && (
                <p className="text-red-500 text-[12px] mt-1">{form.formState.errors.email.message}</p>
              )}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="invite-ticketing-user-invitation-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Invite
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
InviteAsAdminTicketingUserModal.displayName = 'InviteAsAdminTicketingUserModal';

export default InviteAsAdminTicketingUserModal;
